import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

const TermsAndConditions = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow bg-gray-50">
        {/* Page Header */}
        <div className="bg-cecure-900 text-white py-12">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-3xl font-bold">Terms of Service</h1>
            <p className="mt-2">
              Understanding your rights and responsibilities when using CIL
              services
            </p>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Introduction */}
          <Card className="mb-8">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-semibold mb-4">
                What's covered in these terms
              </h2>
              <p className="text-gray-700 mb-4">
                We know it's tempting to skip these Terms of Service, but it's
                important to establish what you can expect from us as you use
                CIL services, and what we expect from you.
              </p>
              <p className="text-gray-700 mb-4">
                These Terms of Service reflect the way CIL's business works, the
                laws that apply to our company, and certain things we've always
                believed to be true. As a result, these Terms of Service help
                define CIL's relationship with you as you interact with our
                services. For example, these terms include the following topic
                headings:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>
                  <strong>What you can expect from us</strong>, which describes
                  how we provide and develop our services
                </li>
                <li>
                  <strong>What we expect from you</strong>, which establishes
                  certain rules for using our services
                </li>
                <li>
                  <strong>Content in CIL services</strong>, which describes the
                  intellectual property rights to the content you find in our
                  services — whether that content belongs to you, CIL, or others
                </li>
                <li>
                  <strong>In case of problems or disagreements</strong>, which
                  describes other legal rights you have, and what to expect in
                  case someone violates these terms
                </li>
              </ul>
              <p className="text-gray-700 mb-4">
                Understanding these terms is important because, by accessing or
                using our services, you're agreeing to these terms.
              </p>
              <p className="text-gray-700">
                Besides these terms, we also publish a Privacy Policy. Although
                it's not part of these terms, we encourage you to read it to
                better understand how you can update, manage, export, and delete
                your information.
              </p>
            </CardContent>
          </Card>

          {/* Service Provider */}
          <Card className="mb-8">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-semibold mb-4">Service provider</h2>
              <p className="text-gray-700 mb-4">
                CIL services are provided by, and you're contracting with:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-medium">
                  Cecure Intelligence Limited ("CIL")
                </p>
                <p className="text-gray-700">
                  organized under the laws of the United Kingdom
                </p>
                <br />
                <p className="text-gray-700">
                  Workshed, 7 Carriage Works
                  <br />
                  London Street, Swindon
                  <br />
                  United Kingdom
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Age Requirements */}
          <Card className="mb-8">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-semibold mb-4">Age requirements</h2>
              <p className="text-gray-700 mb-4">
                If you're under the age required to manage your own Google
                Account, you must have your parent or legal guardian's
                permission to use CIL Services. Please have your parent or legal
                guardian read these terms with you.
              </p>
              <p className="text-gray-700">
                If you're a parent or legal guardian, and you allow your child
                to use CIL Services, then these terms apply to you and you're
                responsible for your child's activity on the services.
              </p>
            </CardContent>
          </Card>

          {/* Your relationship with CIL */}
          <Card className="mb-8">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-semibold mb-4">
                Your relationship with CIL
              </h2>
              <p className="text-gray-700">
                These terms help define the relationship between you and CIL.
                When we speak of "CIL," "we," "us," and "our," we mean Cecure
                Intelligence Limited and its affiliates. Broadly speaking, we
                give you permission to access and use our services if you agree
                to follow these terms.
              </p>
            </CardContent>
          </Card>

          {/* What you can expect from us */}
          <Card className="mb-8">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-semibold mb-4">
                What you can expect from us
              </h2>

              <h3 className="text-xl font-semibold mb-3">
                Provide a broad range of useful services
              </h3>
              <p className="text-gray-700 mb-4">
                We provide a broad range of services that are subject to these
                terms, including:
              </p>
              <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-6">
                <li>apps and sites (like CecureBas)</li>
                <li>platforms (like CecureStream and CecureStream Events)</li>
              </ul>
              <p className="text-gray-700 mb-6">
                Our services are designed to work together, making it easier for
                you to move from one activity to the next.
              </p>

              <h3 className="text-xl font-semibold mb-3">
                Develop, improve, and update CIL services
              </h3>
              <p className="text-gray-700 mb-4">
                We're constantly developing new technologies and features to
                improve our services. As part of this continual improvement, we
                sometimes add or remove features and functionalities, increase
                or decrease limits to our services, and start offering new
                services or stop offering old ones.
              </p>
              <p className="text-gray-700">
                If we make material changes that negatively impact your use of
                our services or if we stop offering a service, we'll provide you
                with reasonable advance notice, except in urgent situations such
                as preventing abuse, responding to legal requirements, or
                addressing security and operability issues.
              </p>
            </CardContent>
          </Card>

          {/* What we expect from you */}
          <Card className="mb-8">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-semibold mb-4">
                What we expect from you
              </h2>

              <h3 className="text-xl font-semibold mb-3">
                Follow these terms and service-specific additional terms
              </h3>
              <p className="text-gray-700 mb-4">
                The permission we give you to access and use our services
                continues as long as you comply with:
              </p>
              <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-6">
                <li>these terms</li>
                <li>
                  service-specific additional terms, where published for
                  specific products
                </li>
              </ul>

              <h3 className="text-xl font-semibold mb-3">Respect others</h3>
              <p className="text-gray-700 mb-4">
                We want to maintain a respectful environment for everyone, which
                means you must follow these basic rules of conduct:
              </p>
              <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-6">
                <li>
                  comply with applicable laws, including export control,
                  sanctions, and human trafficking laws
                </li>
                <li>
                  respect the rights of others, including privacy and
                  intellectual property rights
                </li>
                <li>
                  don't abuse or harm others or yourself (or threaten or
                  encourage such abuse or harm)
                </li>
              </ul>

              <h3 className="text-xl font-semibold mb-3">
                Don't abuse our services
              </h3>
              <p className="text-gray-700 mb-4">
                Most people who access or use our services understand the
                general rules that keep the internet safe and open.
                Unfortunately, a small number of people don't respect those
                rules, so we're describing them here to protect our services and
                users from abuse.
              </p>
              <p className="text-gray-700">
                You must not abuse, harm, interfere with, or disrupt our
                services or systems — for example, by introducing malware,
                spamming, hacking, or bypassing our systems or protective
                measures.
              </p>
            </CardContent>
          </Card>

          {/* Permission to use your content */}
          <Card className="mb-8">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-semibold mb-4">
                Permission to use your content
              </h2>
              <p className="text-gray-700 mb-4">
                Some of our services are designed to let you upload, submit,
                store, send, receive, or share your content. You have no
                obligation to provide any content to our services and you're
                free to choose the content that you want to provide.
              </p>

              <h3 className="text-xl font-semibold mb-3">License</h3>
              <p className="text-gray-700 mb-4">
                Your content remains yours, which means that you retain any
                intellectual property rights that you have in your content. We
                need your permission if your intellectual property rights
                restrict our use of your content. You provide CIL with that
                permission through this license.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold mb-2">What's covered</h4>
                  <p className="text-gray-700 text-sm">
                    This license covers your content if that content is
                    protected by intellectual property rights.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Scope</h4>
                  <p className="text-gray-700 text-sm">
                    This license is worldwide, non-exclusive, and royalty-free.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Using CIL services */}
          <Card className="mb-8">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-semibold mb-4">
                Using CIL services
              </h2>

              <h3 className="text-xl font-semibold mb-3">
                Using CIL services on behalf of an organization or business
              </h3>
              <p className="text-gray-700 mb-4">
                Many organizations, such as businesses, non-profits, and
                schools, take advantage of our services. To use our services on
                behalf of an organization:
              </p>
              <ul className="list-disc pl-6 space-y-1 text-gray-700">
                <li>
                  an authorized representative of that organization must agree
                  to these terms
                </li>
                <li>
                  your organization's administrator might require you to follow
                  additional rules and may be able to access or disable your use
                  of the services
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Content in CIL services */}
          <Card className="mb-8">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-semibold mb-4">
                Content in CIL services
              </h2>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold mb-3">Your content</h3>
                  <p className="text-gray-700">
                    Some of our services allow you to generate original content.
                    CIL won't claim ownership over that content.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">CIL content</h3>
                  <p className="text-gray-700">
                    Some of our services include content that belongs to CIL.
                    You may use CIL's content as allowed by these terms, but we
                    retain any intellectual property rights that we have in our
                    content.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-3">Other content</h3>
                  <p className="text-gray-700">
                    Finally, some of our services give you access to content
                    that belongs to other people or organizations. You may not
                    use this content without that person or organization's
                    permission, or as otherwise allowed by law.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* In case of problems or disagreements */}
          <Card className="mb-8">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-semibold mb-4">
                In case of problems or disagreements
              </h2>

              <h3 className="text-xl font-semibold mb-3">Warranty</h3>
              <p className="text-gray-700 mb-6">
                We provide our services using reasonable skill and care. If we
                don't meet the quality level described in this warranty, you
                agree to tell us and we'll work with you to try to resolve the
                issue.
              </p>

              <h3 className="text-xl font-semibold mb-3">Liabilities</h3>
              <p className="text-gray-700 mb-4">
                Both the law and these terms try to strike a balance as to what
                you or CIL can claim from the other in case of problems. These
                terms only limit our responsibilities as allowed by applicable
                law.
              </p>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-800 mb-2">
                  For business users and organizations only
                </h4>
                <p className="text-yellow-700 text-sm">
                  To the extent allowed by applicable law, you'll indemnify CIL
                  and its directors, officers, employees, and contractors for
                  any third-party legal proceedings arising out of or relating
                  to your unlawful use of the services.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Settling disputes */}
          <Card className="mb-8">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-semibold mb-4">
                Settling disputes, governing law, and courts
              </h2>
              <p className="text-gray-700 mb-4">
                For information about how to contact CIL, please visit our
                contact page.
              </p>
              <p className="text-gray-700 mb-4">
                UK law will govern all disputes arising out of or relating to
                these terms, service-specific additional terms, or any related
                services, regardless of conflict of laws rules. These disputes
                will be resolved exclusively in the courts of the UK.
              </p>
              <p className="text-gray-700">
                To the extent that applicable local law prevents certain
                disputes from being resolved in a UK court, then you can file
                those disputes in your local courts.
              </p>
            </CardContent>
          </Card>

          {/* About these terms */}
          <Card className="mb-8">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-semibold mb-4">About these terms</h2>
              <p className="text-gray-700 mb-4">
                By law, you have certain rights that can't be limited by a
                contract like these terms of service. These terms are in no way
                intended to restrict those rights.
              </p>
              <p className="text-gray-700 mb-4">
                We may update these terms and service-specific additional terms
                to reflect changes in our services or how we do business. If we
                materially change these terms, we'll provide you with reasonable
                advance notice and the opportunity to review the changes.
              </p>
              <p className="text-gray-700">
                If you don't agree to the new terms, you should remove your
                content and stop using the services. You can also end your
                relationship with us at any time by unsubscribing to our
                services or closing any accounts you created.
              </p>
            </CardContent>
          </Card>

          {/* Definitions */}
          <Card className="mb-8">
            <CardContent className="pt-6">
              <h2 className="text-2xl font-semibold mb-4">Definitions</h2>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold">affiliate</h4>
                  <p className="text-gray-700 text-sm">
                    An entity that belongs to the CIL group of companies, which
                    means Cecure Intelligence Limited and its subsidiaries,
                    including Cecure Intelligence Limited (Nigeria)
                  </p>
                </div>

                <Separator />

                <div>
                  <h4 className="font-semibold">business user</h4>
                  <p className="text-gray-700 text-sm">
                    An individual or entity who is not a consumer.
                  </p>
                </div>

                <Separator />

                <div>
                  <h4 className="font-semibold">consumer</h4>
                  <p className="text-gray-700 text-sm">
                    An individual who uses CIL services for personal,
                    non-commercial purposes outside of their trade, business,
                    craft, or profession.
                  </p>
                </div>

                <Separator />

                <div>
                  <h4 className="font-semibold">services</h4>
                  <p className="text-gray-700 text-sm">
                    The CIL services that are subject to these terms include
                    CecureStream, CecureStream Events, CecureBas, and other
                    related services.
                  </p>
                </div>

                <Separator />

                <div>
                  <h4 className="font-semibold">your content</h4>
                  <p className="text-gray-700 text-sm">
                    Things that you create, upload, submit, store, send,
                    receive, or share using our services.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardContent className="pt-6">
              <h2 className="text-2xl font-semibold mb-4">Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about these Terms of Service, please
                contact us at:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700">
                  <strong>Email:</strong> helpdesk@cil.support
                  <br />
                  <strong>Phone:</strong> +234 2013 309293
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TermsAndConditions;
